import { Component, EventEmitter } from '@angular/core';
import {
  DwCardBaseComponent,
  DwComponent,
  DwComponentType,
  DwMetaDataListCardConfigData,
  DwRoutingService
} from '@devwareapps/devware-cap';
import {
  AppMetaDataItemNames,
  AssignmentByStudentQueryEntity,
  RoAssignmentStudentCourseLessonProgressEntity
} from '../../../../../meta-data/app-meta-data.service';

type LessonStatus = 'done' | 'active' | 'next';

@DwComponent<DwMetaDataListCardConfigData>({
  name: 'Assignment Row Card',
  key: 'assignmentRowCard',
  componentType: DwComponentType.card,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Assignment,
  configData: {
    contentItemDefinitions: [{
      display: 'Main',
      contentItemKey: 'main'
    }]
  }
})
@Component({
  selector: 'app-assignment-row-card',
  templateUrl: './assignment-row-card.component.html',
  styleUrls: ['./assignment-row-card.component.scss']
})
export class AssignmentRowCardComponent implements DwCardBaseComponent<any, AssignmentByStudentQueryEntity> {
  cardConfig?: any;
  data?: AssignmentByStudentQueryEntity;
  resolvedContext?: any;
  cardClick: EventEmitter<any> = new EventEmitter<any>();

  /** Cap on incomplete rows rendered in the inline view. */
  readonly maxLessonsToShow = 4;

  completedLessons: RoAssignmentStudentCourseLessonProgressEntity[] = [];
  visibleIncompleteLessons: RoAssignmentStudentCourseLessonProgressEntity[] = [];
  hiddenLessonCount = 0;

  doneCount = 0;
  inProgressCount = 0;
  toGoCount = 0;

  showCompleted = false;

  constructor(private dwRoutingService: DwRoutingService) {}

  setupCard(): void {
    const all = this.data?.AssignmentIdRoAssignmentStudentCourseLessonProgress || [];
    const sorted = [...all].sort(this.lessonOrderCompare);

    this.completedLessons = sorted.filter(l => l.LessonComplete);
    const incomplete = sorted.filter(l => !l.LessonComplete);

    this.doneCount = this.completedLessons.length;
    this.inProgressCount = incomplete.filter(l => (l.PercentComplete ?? 0) > 0).length;
    this.toGoCount = incomplete.length - this.inProgressCount;

    // Active lesson floats to the top of the incomplete list so the Resume
    // row is always the first thing the learner sees.
    incomplete.sort((a, b) => {
      const aActive = (a.PercentComplete ?? 0) > 0 ? 0 : 1;
      const bActive = (b.PercentComplete ?? 0) > 0 ? 0 : 1;
      return aActive - bActive || this.lessonOrderCompare(a, b);
    });

    this.visibleIncompleteLessons = incomplete.slice(0, this.maxLessonsToShow);
    this.hiddenLessonCount = Math.max(0, incomplete.length - this.maxLessonsToShow);
  }

  get courseDisplay(): string {
    return this.data?.CourseIdDisplay || '';
  }

  /** "2 done · 1 in progress · 2 to go" — segments hide themselves when 0. */
  get statSegments(): { label: string; count: number; tone: 'done' | 'active' | 'next' }[] {
    const segments: { label: string; count: number; tone: 'done' | 'active' | 'next' }[] = [
      { label: 'done', count: this.doneCount, tone: 'done' },
      { label: 'in progress', count: this.inProgressCount, tone: 'active' },
      { label: 'to go', count: this.toGoCount, tone: 'next' }
    ];
    return segments.filter(s => s.count > 0);
  }

  /** True when every lesson has LessonComplete=true (data-derived, distinct from CompletedDateTime). */
  get allLessonsComplete(): boolean {
    return this.doneCount > 0 && this.inProgressCount === 0 && this.toGoCount === 0;
  }

  get isOverdue(): boolean {
    const ts = this.data?.DueDateTime;
    if (!ts || this.data?.CompletedDateTime) {
      return false;
    }
    const due = new Date(ts);
    return !isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  /** Short pill format used in the header — e.g. "189 days" or "189 days late". */
  get shortDueLabel(): string | null {
    const ts = this.data?.DueDateTime;
    if (!ts || this.data?.CompletedDateTime) {
      return null;
    }
    const due = new Date(ts);
    if (isNaN(due.getTime())) {
      return null;
    }
    const dayMs = 86400000;
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(due) - startOfDay(new Date())) / dayMs);
    if (diffDays >= 0) {
      return null;
    }
    const overdueBy = Math.abs(diffDays);
    const dayWord = overdueBy === 1 ? '1 day' : `${overdueBy} days`;
    return this.allLessonsComplete ? `${dayWord} late` : dayWord;
  }

  toggleCompleted(): void {
    this.showCompleted = !this.showCompleted;
  }

  lessonStatus(lesson: RoAssignmentStudentCourseLessonProgressEntity): LessonStatus {
    if (lesson.LessonComplete) {
      return 'done';
    }
    if ((lesson.PercentComplete ?? 0) > 0) {
      return 'active';
    }
    return 'next';
  }

  /** Secondary line under the title — progress for active, blank for next/done. */
  lessonMeta(lesson: RoAssignmentStudentCourseLessonProgressEntity): string | null {
    const status = this.lessonStatus(lesson);
    if (status === 'active') {
      const pct = lesson.PercentComplete;
      if (typeof pct === 'number' && pct > 0) {
        return `${Math.round(pct)}% complete`;
      }
      return 'In progress';
    }
    return null;
  }

  clickLesson(lesson: RoAssignmentStudentCourseLessonProgressEntity, event: Event): void {
    event.stopPropagation();
    const courseId = (lesson as any).CourseId;
    const moduleId = (lesson as any).CourseModuleId;
    const lessonId = (lesson as any).CourseLessonId;
    if (courseId && moduleId && lessonId) {
      this.dwRoutingService.navigateToUrl(
        `/my-courses/view/${courseId}/module/${moduleId}/lesson/${lessonId}`,
        null
      );
    }
  }

  clickGroupHeader(): void {
    const id = this.data?.AssignmentStudentAssignmentStudentId;
    if (id) {
      this.dwRoutingService.navigateToUrl(`/my-assignments/view/${id}`, null);
    }
  }

  private lessonOrderCompare = (
    a: RoAssignmentStudentCourseLessonProgressEntity,
    b: RoAssignmentStudentCourseLessonProgressEntity
  ): number => {
    const am = (a as any).ModuleOrder ?? 0;
    const bm = (b as any).ModuleOrder ?? 0;
    if (am !== bm) {
      return am - bm;
    }
    const al = (a as any).LessonOrder ?? 0;
    const bl = (b as any).LessonOrder ?? 0;
    return al - bl;
  };
}
